import { ButtonComponent, IconButton } from '@/components/commons'
import { closeIconButtonContentCategories } from '@/components/commons/buttons/styles/IconButton.css'
import { Text } from '@/components/commons/typography/Text'
import { PlusIcon } from '@/public/assets/v2/images/icons'
import { CONTENT_TYPE_TAG } from '@/shared/consts/tags'
import { useSortTags } from '@/shared/hooks/getTags'
import { cssClass } from '@/shared/utils'
import { TagsSelectorModal } from 'containers/edit-profile/components/edit-form/TagsSelectorModal'
import { useHighlightEditorStore } from 'containers/highlights/store/highlightEditor'
import { useUser } from 'containers/user/hooks/useUser'
import { useMemo, useState } from 'react'

export const HContentCategories = () => {
  const { user } = useUser()
  const { highlight, updateHighlight } = useHighlightEditorStore()
  const [showTagModal, setShowTagModal] = useState(false)
  const [tagsList] = useSortTags(CONTENT_TYPE_TAG)
  const [recommendedTagsRemoved, setRecommendedTagsRemoved] = useState<
    string[]
  >([])

  const recommendedTags = useMemo(() => {
    if (!user?.contentType?.length) return []
    return user?.contentType?.filter(
      (type) =>
        !recommendedTagsRemoved.includes(type) &&
        !highlight?.contentCategories?.includes(type)
    )
  }, [highlight?.contentCategories, recommendedTagsRemoved, user?.contentType])

  const removeRecommendedTag = (tag: string) => {
    setRecommendedTagsRemoved([...recommendedTagsRemoved, tag])
  }

  const toggleTag = (tag: string) => {
    const newTags = [...(highlight?.contentCategories || [])]
    const index = newTags?.findIndex((t) => t === tag)
    if (index > -1) {
      newTags.splice(index, 1)
    } else {
      newTags.push(tag)
    }
    updateHighlight({ contentCategories: newTags })
  }

  const addAllRecommendedTags = () => {
    const newTags = [...(highlight?.contentCategories || [])]
    newTags.push(...recommendedTags)
    updateHighlight({ contentCategories: newTags })
    setRecommendedTagsRemoved([])
  }

  const Tag = ({
    type,
    withCloseButton,
    active = true,
  }: {
    type: string
    withCloseButton?: boolean
    active?: boolean
  }) => (
    <div
      key={type}
      onClick={() => toggleTag(type)}
      className={cssClass([
        'px-16 py-8 border rounded-8 flex items-center gap-8 cursor-pointer',
        active ? 'bg-accent-tertiary-light' : 'bg-tertiary',
      ])}
    >
      <Text
        variation="xSmall"
        colorClass={active ? 'text-accent-tertiary' : 'text-tertiary'}
        lineHeightClass="leading-16"
      >
        {type}
      </Text>
      {withCloseButton && (
        <IconButton
          icon={'CloseIcon'}
          className={closeIconButtonContentCategories}
          iconClassName={cssClass([
            'text-12',
            active ? 'text-info' : 'text-tertiary',
          ])}
          onClick={(e) => {
            if (active) {
              toggleTag(type)
              return
            }
            e.stopPropagation()
            removeRecommendedTag(type)
          }}
        />
      )}
    </div>
  )

  return (
    <div className="flex flex-column gap-8">
      <TagsSelectorModal
        openModal={showTagModal}
        title="Content Tags"
        description="Select the content categories that best describe your Highlight."
        withSearch={true}
        tagList={tagsList}
        selectedTags={highlight?.contentCategories || []}
        toggleTag={toggleTag}
        closeDrawer={() => setShowTagModal(false)}
      />

      {recommendedTags?.length > 0 && (
        <>
          <Text variation="small" colorClass="text-tertiary">
            Suggested based on your profile - tap to add to your project
          </Text>
          <div className="flex gap-8 wrap">
            {recommendedTags?.map((type) => (
              <Tag
                key={type + '_recommended'}
                type={type}
                withCloseButton
                active={false}
              />
            ))}
            <ButtonComponent
              size="xSmall"
              variation="secondary"
              label="Add All"
              onClick={addAllRecommendedTags}
              iconLeft={<PlusIcon className="text-tertirary" />}
            />
          </div>
        </>
      )}

      <ButtonComponent
        size="small"
        label="🎬 Add Tags"
        iconRight={<PlusIcon className="text-secondary text-20" />}
        variation="white-bg"
        fullWidth
        onClick={() => setShowTagModal(true)}
      />
      <div className="flex gap-8 wrap">
        {highlight?.contentCategories?.map((type) => (
          <Tag key={type + '_highlight_tag'} type={type} withCloseButton />
        ))}
      </div>
    </div>
  )
}
